import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import type { UserRole } from '../lib/provider/types';

export const OnboardingPage: React.FC = () => {
    const { session, createHousehold, joinHousehold, isLoading, signOut } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const searchParams = new URLSearchParams(window.location.search);
    const inviteHouseholdId = searchParams.get('householdId') || '';
    const inviteRoleParam = searchParams.get('role');
    const inviteRole: UserRole | '' = inviteRoleParam === 'parent' || inviteRoleParam === 'kid' ? inviteRoleParam : '';

    const [mode, setMode] = useState<'create' | 'join'>(inviteHouseholdId ? 'join' : 'create');
    const [householdName, setHouseholdName] = useState('');
    const [householdId, setHouseholdId] = useState(inviteHouseholdId);
    const [displayName, setDisplayName] = useState('');
    const [role, setRole] = useState<UserRole>(inviteRole || 'parent');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    if (isLoading) return <div className="p-8 text-center text-slate-500 font-bold">{t.common.loading}</div>;

    // If not authenticated at all, go to login
    if (!session.isAuthenticated) {
        const next = `${location.pathname}${location.search}`;
        const authPath = inviteHouseholdId ? '/register' : '/login';
        return <Navigate to={`${authPath}?next=${encodeURIComponent(next)}`} replace />;
    }

    // If already has a role/household, redirected by App.tsx logic usually, 
    // but let's be safe:
    if (session.user) {
        return <Navigate to={session.user.role === 'parent' ? '/parent' : '/kid'} replace />;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            if (mode === 'create') {
                await createHousehold(householdName, displayName);
            } else {
                await joinHousehold(householdId, displayName, role);
            }
        } catch (err: any) {
            setError(err.message || `Failed to ${mode} household`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                <h1 className="text-3xl font-bold text-center text-slate-900 mb-2">{t.onboarding.welcome}</h1>
                <p className="text-center text-slate-500 mb-8">{t.onboarding.subtitle}</p>

                <div className="flex bg-slate-100 p-1 rounded-lg mb-6">
                    <button
                        onClick={() => setMode('create')}
                        className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${mode === 'create' ? 'bg-white shadow-sm text-primary-600' : 'text-slate-500'}`}
                    >
                        {t.onboarding.createTab}
                    </button>
                    <button
                        onClick={() => setMode('join')}
                        className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${mode === 'join' ? 'bg-white shadow-sm text-primary-600' : 'text-slate-500'}`}
                    >
                        {t.onboarding.joinTab}
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-6 border border-red-100">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">{t.onboarding.parentName}</label>
                        <input
                            type="text"
                            required
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="e.g. Mom"
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 outline-none"
                        />
                    </div>

                    {mode === 'create' ? (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t.onboarding.householdName}</label>
                            <input
                                type="text"
                                required
                                value={householdName}
                                onChange={(e) => setHouseholdName(e.target.value)}
                                placeholder="e.g. The Smiths"
                                className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                        </div>
                    ) : (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">{t.onboarding.joinCode}</label>
                                <input
                                    type="text"
                                    required
                                    value={householdId}
                                    onChange={(e) => setHouseholdId(e.target.value)}
                                    placeholder="Paste the household ID here"
                                    className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-500 outline-none font-mono text-sm"
                                />
                                <p className="text-xs text-slate-500 mt-1">{t.onboarding.askMember}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">{t.onboarding.yourRole}</label>
                                <div className="grid grid-cols-2 gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setRole('parent')}
                                        className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${role === 'parent' ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
                                    >
                                        <div className="text-2xl mb-1">👑</div>
                                        <span className="font-semibold text-xs">Parent</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setRole('kid')}
                                        className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${role === 'kid' ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
                                    >
                                        <div className="text-2xl mb-1">🎈</div>
                                        <span className="font-semibold text-xs">Kid</span>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-primary-600 text-white py-2 rounded-lg font-bold hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                        {submitting ? t.common.loading : (mode === 'create' ? t.onboarding.start : t.onboarding.join)}
                    </button>

                    <button
                        type="button"
                        onClick={() => signOut()}
                        className="w-full text-slate-500 text-sm mt-4 hover:text-slate-700 transition-colors"
                    >
                        {t.common.sign_out}
                    </button>
                </form>
            </div>
        </div>
    );
};
